import { classnames } from 'Utils/classnames/classnames.utils';
import React from 'react';
import type { ActionButtonsGroupItemProps } from 'Components/frameworks/action-buttons-group/ActionButtonsGroup';
import { ActionButtonsGroup } from 'Components/frameworks/action-buttons-group/ActionButtonsGroup';
import Image from 'Components/frameworks/image/Image';
import { YoukuThumbnail } from 'Components/frameworks/image/YoukuThumbnail';
import { ImageSlideShow } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import type { MediaThumbnailProps } from 'Components/frameworks/media-thumbnail/MediaThumbnail';
import { MediaThumbnail } from 'Components/frameworks/media-thumbnail/MediaThumbnail';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
const YOUKU_SRC = 'vthumb.ykimg.com';
export type MultimediaLauncherProps = {
  buttons: Array<ActionButtonsGroupItemProps>;
  className?: string;
  closeLabel?: string;
  galleryLabel?: string;
  hasLazyLoading?: boolean;
  isExclusiveCollection?: boolean;
  isImageSlideShow?: boolean;
  isOverlay?: boolean;
  medias: Array<MediaThumbnailProps>;
  nextLabel?: string;
  previousLabel?: string;
  theme?: string;
};

function MultimediaLauncherThumbnail(props) {
  return props.src ? (
    <MediaThumbnail {...props} hasLazyLoading={props.hasLazyLoading}>
      {props.src.includes(YOUKU_SRC) ? (
        <YoukuThumbnail imageUrl={props.src} ratioKey={props.ratio} />
      ) : null}
    </MediaThumbnail>
  ) : null;
}

export function MultimediaLauncher({
  buttons,
  className,
  closeLabel,
  galleryLabel,
  hasLazyLoading,
  isExclusiveCollection,
  isImageSlideShow,
  isOverlay,
  medias = [],
  nextLabel,
  previousLabel,
  theme,
}: MultimediaLauncherProps) {
  const { isTablet } = useResponsive();
  const defaultTheme = isOverlay ? 'overlay' : 'default';
  const computedTheme = theme || defaultTheme;
  return (
    <div
      className={classnames(
        'w-full overflow-hidden',
        {
          'rounded-12': computedTheme === 'default',
        },
        className,
      )}
    >
      {!isOverlay &&
        !isImageSlideShow &&
        (isTablet ? (
          <div className="animation-duration-500 -m-1 flex animate-fadeIn border-b-1 border-transparent">
            <div className="w-1/2 p-1">
              <MultimediaLauncherThumbnail
                {...medias[0]}
                hasLazyLoading={hasLazyLoading}
                ratioDisplay={1 / 2}
              />
            </div>
            <div className="flex w-1/2 flex-wrap">
              {medias.slice(1, 5).map((media) => {
                return (
                  <div className="w-1/2 p-1" key={`MultimediaLauncherThumbnail-${media.src}`}>
                    <MultimediaLauncherThumbnail
                      {...media}
                      hasLazyLoading={hasLazyLoading}
                      ratioDisplay={1 / 4}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="animation-duration-500 animate-fadeIn border-b-2 border-transparent">
            <MultimediaLauncherThumbnail {...medias[0]} hasLazyLoading={hasLazyLoading} />
          </div>
        ))}
      {!isOverlay && isImageSlideShow && (
        <div className="animation-duration-500 animate-fadeIn">
          {medias.length > 0 ? (
            <ImageSlideShow
              className={className}
              closeLabel={closeLabel}
              galleryLabel={galleryLabel}
              hasInfiniteScroll
              hasLazyLoading={false}
              media={medias.map(({ alt, onClick, src }) => ({
                alt,
                onClick,
                src,
              }))}
              nextLabel={nextLabel}
              previousLabel={previousLabel}
              ratio={ratioUtils.XL}
            />
          ) : (
            <Image
              altMsg={medias[0].alt}
              hasLazyLoading={false}
              imageUrl={medias[0].src}
              ratioKey={ratioUtils.XL}
            />
          )}
        </div>
      )}
      <ActionButtonsGroup
        className="animation-duration-500 animate-fadeIn"
        isExclusiveCollection={isExclusiveCollection}
        items={buttons}
        minWidth={isOverlay ? '6.25rem' : '8.4375rem'}
        theme={computedTheme}
      />
    </div>
  );
}
